import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import RootLayout from '../components/RootLayout';
import Layout from '../components/Layout';
import Home from '../pages/Home';
import Events from '../pages/Events';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Profile from '../pages/Profile';
import ProfileEdit from '../pages/ProfileEdit';
import OrganizerDashboard from '../pages/OrganizerDashboard';
import CreateEvent from '../pages/CreateEvent';
import About from '../pages/About';
import Contact from '../pages/Contact';
import Bookings from '../pages/Bookings';
import AdminDashboard from '../pages/AdminDashboard';
import NotFound from '../pages/NotFound';
import RouteDiagnostic from '../pages/RouteDiagnostic';
import EventDetails from '../pages/EventDetails';
import { useAuth, Role } from '../contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';
import SavedEvents from '../pages/SavedEvents';
import EditEvent from '../pages/EditEvent';

// Route protection component props
interface ProtectedRouteProps {
  requiresAuth?: boolean;
  requiredRoles?: string[];
}

// Route protection components
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiresAuth = true, requiredRoles = [] }) => {
  const { user, loading, userRoles } = useAuth();
  
  // Show a simple loading indicator when auth is being determined
  // but don't wait too long to prevent infinite loading
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Loading authentication...</p>
      </div>
    );
  }
  
  // If route requires authentication and user is not logged in, redirect to login
  if (requiresAuth && !user) {
    return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  }
  
  // Check if user has required roles (if any)
  if (user && requiredRoles.length > 0) {
    // Use userRoles from context instead of user.roles which may not exist
    const hasRequiredRole = requiredRoles.some(role => 
      userRoles.includes(role as Role)
    );
    
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }
  
  // All checks passed, render the protected route
  return <Outlet />;
};

// Main application routes
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: "/",
        element: <Layout />,
        children: [
          // Public routes (no auth required)
          {
            index: true,
            element: <Home />,
          },
          {
            path: "/events",
            element: <Events />,
          },
          {
            path: "/events/:id",
            element: <EventDetails />,
          },
          {
            path: "/about",
            element: <About />,
          },
          {
            path: "/contact",
            element: <Contact />,
          },
          {
            path: "/login",
            element: <Login />,
          },
          {
            path: "/register",
            element: <Register />,
          },
          {
            path: "/diagnostic",
            element: <RouteDiagnostic />,
          },
          {
            path: "/events/category/:category",
            element: <Events key="category" />, // TEMP: Use Events page for category filtering, will create EventCategoryPage next
          },
          
          // Protected routes (auth required)
          {
            element: <ProtectedRoute requiresAuth={true} />,
            children: [
              {
                path: "/profile",
                element: <Profile />,
              },
              {
                path: "/profile/edit",
                element: <ProfileEdit />,
              },
              {
                path: "/bookings",
                element: <Bookings />,
              },
              {
                path: "/saved-events",
                element: <SavedEvents />,
              },
            ]
          },
          
          // Organizer/Admin routes
          {
            element: <ProtectedRoute requiresAuth={true} requiredRoles={['organizer', 'admin']} />,
            children: [
              {
                path: "/events/create",
                element: <CreateEvent />,
              },
              {
                path: "/events/:id/edit",
                element: <EditEvent />,
              },
              {
                path: "/dashboard",
                element: <OrganizerDashboard />,
              },
            ]
          },
          
          // Admin-only routes
          {
            element: <ProtectedRoute requiresAuth={true} requiredRoles={['admin']} />,
            children: [
              {
                path: "/admin/dashboard",
                element: <AdminDashboard />,
              },
            ]
          },
          
          // Fallback 404 route
          {
            path: "*",
            element: <NotFound />,
          }
        ],
      },
    ],
  },
]);

export default router; 